import * as PIXI from "pixi.js";
import { FONT_STYLES, scaleStyle } from "../fontStyles";
import { getTapWordAnchor } from "./tapWordAnchor";

// The shared numbers. Every popup renders the word at 42 and takes an 80-tall
// hit area, so they are named here rather than repeated.
export const TAP_TO_CONTINUE_FONT_SIZE = 42;
export const TAP_TO_CONTINUE_HIT_HEIGHT = 80;
// Used only when a spine does not key the tap slot at all.
export const TAP_TO_CONTINUE_FALLBACK_MS = 1000;
const TAP_TO_CONTINUE_FALLBACK_WIDTH = 400;


/**
 * The tap-to-continue word, built the one way every popup builds it.
 *
 * This exact block is written out by hand in five Zeus controllers —
 * PotCollectionOutro, PotCollectionGrandJackpot, PotCollectionMaxWin,
 * PotCollectionReload and WinAnimationSecondary — with the same font size, the
 * same bone-width fit and the same hit area. Five copies is five chances to
 * drift, and the whole point is that the button reads identically everywhere.
 *
 * What the shared rule is:
 *   - size 42, scaled from the style's authored size by scaleStyle
 *   - anchored centre, pinned into the spine's own slot so the bone places it
 *     and the in/out fades carry it
 *   - shrunk to the BONE's length if the word overruns, never scaled up
 *   - hit area (-boneWidth / 2, -40, boneWidth, 80)
 *
 * @param {Object}   p
 * @param {Object}   p.spine       Host Spine instance.
 * @param {string[]} p.slotNames   Candidate slots, first hit wins.
 * @param {string}   p.label       Already localised and upper-cased.
 * @param {Object}  [p.style]      fontStyles entry. Defaults to tapToContinue.
 * @param {number}  [p.fontSize]   Rendered size. The shared 42 by default.
 * @param {Function}[p.onClick]    Attaches pointer handling when given.
 * @param {string}  [p.name]       Container name, for the scene graph.
 * @param {boolean} [p.anchored]   Match the intro popup's word exactly — see
 *                                 the anchor block below. On by default,
 *                                 because "all popups look the same" is the
 *                                 rule, not the exception.
 * @returns {{container: PIXI.Container, text: PIXI.Text, slotName: string,
 *            boneWidth: number, fit: Function}|null}
 */
export function attachTapToContinue({
  spine,
  slotNames,
  label,
  style = FONT_STYLES.tapToContinue,
  fontSize = TAP_TO_CONTINUE_FONT_SIZE,
  onClick = null,
  name = "tap_to_continue_attached_to_slot",
  anchored = true,
}) {
  const skeleton = spine?.skeleton;
  if (!skeleton || !style) return null;

  let slotName = null;
  for (const candidate of slotNames || []) {
    if (skeleton.findSlot(candidate)) {
      slotName = candidate;
      break;
    }
  }
  if (!slotName) {
    console.warn("[tapToContinue] No slot found:", slotNames);
    return null;
  }

  const container = new PIXI.Container();
  container.name = name;

  const text = new PIXI.Text(
    label,
    new PIXI.TextStyle(scaleStyle(style, fontSize)),
  );
  text.resolution = 2;
  text.anchor.set(0.5);
  container.addChild(text);

  // The bone's length is the artist's statement of how much room the word has.
  const bone = skeleton.findSlot(slotName)?.bone;
  const boneWidth = bone?.data?.length || TAP_TO_CONTINUE_FALLBACK_WIDTH;

  /**
   * Where this slot's bone rests, in SETUP-POSE skeleton units.
   *
   * The setup pose and not the live `worldY`, because `in` keys this slot: read
   * live, the answer depends on which frame the popup happens to be on. The
   * setup pose is the resting intent, and it is the same number the controllers
   * were already quoting by hand ("its bone sits at -480.24").
   *
   * Summed up the parent chain. No rotation or scale in it — these tap bones
   * hang straight off the root or one holder, which is the same assumption the
   * hand-tuned constants made.
   */
  const boneSetupY = (() => {
    let y = 0;
    for (let b = bone; b; b = b.parent) y += b.data?.y || 0;
    return y;
  })();

  /**
   * The spine's position and scale in SCREEN space.
   *
   * ── Not `spine.y`, which is the mistake this replaced ──
   *
   * Every one of these dialogs sets `spine.x = 0` and `spine.y` to a small
   * offset — those are local to a container the dialog centres on the screen,
   * so `spine.y` is nowhere near a screen coordinate. Reading it as one put the
   * word half a screen out.
   *
   * The world transform is the same answer whatever the parent chain does, and
   * `getGlobalPosition(p, false)` forces it up to date first, so this does not
   * depend on a render having happened. `d` is the world Y scale; there is no
   * rotation anywhere in these popups' chains.
   *
   * Falls back to the local values when there is no transform to read — before
   * the spine is in the tree, and in tests.
   */
  const spineWorld = () => {
    let wt = null;
    try {
      // Forces the parent chain up to date, so this does not depend on a render
      // having happened. Throws when a parent is not in the tree yet.
      spine?.getGlobalPosition?.(undefined, false);
      wt = spine?.worldTransform ?? null;
    } catch {
      wt = null;
    }
    if (wt && Number.isFinite(wt.ty) && wt.d) {
      return { y: wt.ty, scale: wt.d };
    }
    // ── NULL, never the local values ──
    //
    // `spine.y` is a small offset inside a container the dialog centres on the
    // screen, so handing it back as a screen coordinate places the word
    // hundreds of pixels away — off the popup, and to the player simply gone.
    // That is what happened on every resize: the transform is briefly unusable
    // while the new layout is applied, the fallback fired, and the word
    // vanished instead of re-fitting. Vladimir, 2026-09-22: "every time i
    // resize my screen the text doesnt recalculate itself it just disappears."
    //
    // No trustworthy transform means no answer. The caller keeps the last good
    // placement and tries again on the next pass.
    return null;
  };

  // The solved local y, or null when there is no anchor to solve against.
  let anchorLocalY = null;
  let manualOffsetY = 0;
  const placeY = () => {
    text.y = anchorLocalY !== null ? anchorLocalY : manualOffsetY;
  };

  /**
   * Re-fit after a resize or a text change.
   *
   * Capped by the bone AND by the screen, because a long translation on a
   * narrow phone can overrun even a generous bone. `dimensions` is read at
   * call time so it always sees the current viewport.
   */
  // The one deferred re-fit in flight, if any.
  let refitRAF = null;

  /**
   * Fit again on the next frame, once.
   *
   * ── Why a second pass at all ──
   *
   * A resize reaches this through the controller's own `updateDimensions`, and
   * at that moment the parent containers may still carry the transform for the
   * OLD screen size — React has re-rendered the dialog but PIXI has not yet
   * applied it. Solved from that, the word lands on the previous layout's line.
   *
   * One frame later everything has settled, so the answer is simply taken
   * again. A single rAF per fit, replaced rather than stacked, and the deferred
   * pass never schedules another — so this can cost at most one extra frame of
   * work per resize and can never become a loop.
   */
  const scheduleRefit = (dimensions, deferred) => {
    if (deferred) return;
    if (typeof requestAnimationFrame !== "function") return;
    if (refitRAF !== null) cancelAnimationFrame(refitRAF);
    refitRAF = requestAnimationFrame(() => {
      refitRAF = null;
      fit(dimensions, { deferred: true });
    });
  };

  const fit = (dimensions, { deferred = false } = {}) => {
    // ── `transform`, not `destroyed` ──
    //
    // PIXI v7 keeps the destroyed flag PRIVATE (`_destroyed`), so a guard on
    // `spine.destroyed` reads undefined and never fires. `destroy()` nulls
    // `transform`, and every read below goes through it — `worldTransform` is a
    // getter on it, and so is the text's own scale. So this is both the honest
    // test and the one that covers what follows.
    //
    // It matters because of the deferred pass: a popup closed during the frame
    // after a resize would run this on a torn-down spine.
    if (!spine?.transform) return;
    // Scales the TEXT, not the container. updateSlotObject rewrites the
    // container's whole transform from the bone every frame, so anything set
    // on the container is gone by the next render — the Pot Collection outro
    // hit this and left a comment saying so. The text is a child, and the
    // runtime never touches it.
    // SCREEN scale, not the spine's own: the cap below converts a screen width
    // into the text's local units, and the parent chain is part of that.
    const world = spineWorld();
    const anchor = anchored ? getTapWordAnchor() : null;

    // An anchor with nothing to solve against. Leave the word exactly where it
    // is rather than placing it from numbers that are not screen coordinates —
    // the deferred pass below will do it properly once the layout has settled.
    if (anchor && !world) {
      scheduleRefit(dimensions, deferred);
      return;
    }

    text.scale.set(1);
    const spineScale = world ? world.scale : spine?.scale?.x || 1;
    const naturalWidth = text.width;

    // ── Solve back to the intro popup's word ──
    //
    // The anchor is a SCREEN position and a SCREEN size, measured by the intro
    // (see utils/tapWordAnchor). This spine draws at its own scale somewhere on
    // that screen, so both are converted into its local space:
    //
    //   rendered size = fontSize x text.scale x spineScale
    //   screen y      = spine.y - boneSetupY x spineScale + text.y x spineScale
    //
    // The skeleton is y-up and PIXI is y-down, which is the minus on the bone.
    //
    // Null anchor — the intro has not laid out yet — leaves both alone, and the
    // popup keeps exactly the size and offset its own rig gave it.
    let scale = 1;
    if (anchor) {
      scale = anchor.fontSize / (fontSize * world.scale);
      const boneScreenY = world.y - boneSetupY * world.scale;
      anchorLocalY = (anchor.y - boneScreenY) / world.scale;
    } else {
      anchorLocalY = null;
    }

    // The bone's length still caps the WIDTH, whatever the anchor says. That is
    // the artist's statement about this rig and the anchor knows nothing about
    // it — a long translation has to fit the plate it is drawn on.
    const screenLocal = ((dimensions?.width || 800) * 0.8) / spineScale;
    const maxWidth = Math.min(boneWidth, screenLocal);
    if (maxWidth > 0 && naturalWidth * scale > maxWidth) {
      scale = maxWidth / naturalWidth;
    }

    text.scale.set(scale);
    placeY();
    scheduleRefit(dimensions, deferred);
  };

  /**
   * Nudge the word off its bone, in spine-local units.
   *
   * Same reason as the fit above: set on the text, because the container's
   * transform belongs to the bone. Negative lifts.
   *
   * IGNORED while an anchor is in play, and deliberately not removed from the
   * callers. These offsets were each tuned by hand, per device, in four
   * controllers, to line the popups up with EACH OTHER; the anchor lines them
   * up with the start page, which is the actual requirement. Both applied would
   * be the old correction on top of the new one. Kept as the fallback for a
   * popup that somehow opens before the intro has measured.
   */
  const setOffsetY = (y) => {
    manualOffsetY = y || 0;
    placeY();
  };
  fit(null);

  spine.addSlotObject(slotName, container);

  if (onClick) {
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new PIXI.Rectangle(
      -boneWidth / 2,
      -TAP_TO_CONTINUE_HIT_HEIGHT / 2,
      boneWidth,
      TAP_TO_CONTINUE_HIT_HEIGHT,
    );
    container.on("pointerdown", (evt) => {
      if (evt?.stopPropagation) evt.stopPropagation();
      onClick(evt);
    });
  }

  /**
   * When the tap word has finished appearing in `animationName`, in ms.
   *
   * Every popup used to unlock input on a flat 1000ms timer. That happened to
   * match the free-spin intro, whose artist reveals the word at exactly 1.0s —
   * and was wrong everywhere else. The additional-free-spins spine reveals it
   * at 0.333s of a 1.667s `in`, so the word sat there ignoring taps for two
   * thirds of a second, which is precisely what "feels different to click"
   * means.
   *
   * Reading it from the spine makes every popup accept a tap the moment it
   * looks like it should, whatever the artist chose.
   *
   * Timeline.getDuration() is the last keyframe's time and `slotIndex` is on
   * every slot-scoped timeline; both are public runtime API. Walking the raw
   * `frames` array by hand needs the per-type frame stride and gets it wrong
   * quietly.
   *
   * @param {string} animationName
   * @param {number} fallback Used when the slot carries no timeline at all.
   */
  const revealMs = (animationName, fallback = TAP_TO_CONTINUE_FALLBACK_MS) => {
    const animation = skeleton.data?.animations?.find(
      (a) => a.name === animationName,
    );
    const slotIndex = skeleton.findSlot(slotName)?.data?.index;
    if (!animation || slotIndex === undefined) return fallback;

    let last = 0;
    for (const timeline of animation.timelines || []) {
      if (timeline.slotIndex !== slotIndex) continue;
      if (typeof timeline.getDuration !== "function") continue;
      last = Math.max(last, timeline.getDuration());
    }

    const ms = Math.round(last * 1000);
    return ms > 0 ? ms : fallback;
  };

  return { container, text, slotName, boneWidth, fit, setOffsetY, revealMs };
}

export default attachTapToContinue;
